import React, {useCallback, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material'

import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {CreateAccountSubProps} from '@progyny/pages/CreateAccount/types'
import {useMeStore} from '@progyny/store/meStore'
import {REACT_APP_DOMAIN_NAME} from '@progyny/utils/env'

const Consent: React.ComponentType<CreateAccountSubProps> = ({
  navigator,
  persistMemberData,
  initialValue,
  callback,
  updateLoading,
}) => {
  const {t} = useTranslation()
  const isMobile = useResponsive('down', 'md')
  const companyHasBudget = useMeStore((s) => s.companyHasBudget)
  const user = useMeStore((s) => s.user)
  const [checks, setChecks] = useState<{
    acceptTermsConditions: boolean
    acceptPrivacyStatement: boolean
  }>(() => ({
    acceptTermsConditions: Boolean(
      user?.member?.acceptedTermsConditions ??
      initialValue.acceptTermsConditions
    ),
    acceptPrivacyStatement: Boolean(
      user?.member?.acceptedPrivacyStatement ??
      initialValue.acceptPrivacyStatement
    ),
  }))

  const Navigator = navigator

  const next = useCallback(async () => {
    try {
      await persistMemberData(checks)
      callback()
    } catch {
      // Mutation failed; stay on consent so the user can retry.
    }
  }, [checks, persistMemberData, callback])

  const budgetTermsAndConditionsLink = `https://${REACT_APP_DOMAIN_NAME}/terms-members`
  const noBudgetTermsAndConditionsLink = `https://${REACT_APP_DOMAIN_NAME}/terms-members-nb`
  const budgetPrivacyPolicyLink = `https://${REACT_APP_DOMAIN_NAME}/privacy-policy`
  const noBudgetPrivacyPolicyLink = `https://${REACT_APP_DOMAIN_NAME}/privacy-policy-nb`

  const termsAndConditionsLink = companyHasBudget
    ? budgetTermsAndConditionsLink
    : noBudgetTermsAndConditionsLink

  const privacyPolicyLink = companyHasBudget
    ? budgetPrivacyPolicyLink
    : noBudgetPrivacyPolicyLink

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}
    >
      <Typography gutterBottom variant='h5'>
        {t('create_account_consent_this_is_how_we_want')}
      </Typography>
      <Stack spacing={{xs: 3, md: 6}} mt={{xs: 3, md: 6}} width='100%'>
        <Box>
          <Typography gutterBottom variant='subtitle1'>
            {t('create_account_consent_data_privacy')}
          </Typography>
          <Typography variant='body2'>
            <Trans i18nKey='create_account_consent_your_personal_data_including_your' />
          </Typography>
        </Box>
        <Box>
          <Typography gutterBottom variant='subtitle1'>
            {t('create_account_consent_marketing')}
          </Typography>
          <Typography variant='body2'>
            <Trans i18nKey='create_account_consent_with_your_consent_we_will' />{' '}
            <Link
              fontSize={'inherit'}
              href={`mailto:support@${REACT_APP_DOMAIN_NAME}`}
              underline='hover'
            >
              support@{REACT_APP_DOMAIN_NAME}
            </Link>
            .
          </Typography>
        </Box>
      </Stack>
      <Grid
        container
        justifyItems='center'
        justifyContent='center'
        mt={{xs: 3, md: 4}}
      >
        <Grid item xs={12} sm={9}>
          <FormControlLabel
            sx={{
              alignItems: 'flex-start',
              marginBottom: 2,
            }}
            control={
              <Checkbox
                checked={checks.acceptTermsConditions}
                onChange={() => {
                  setChecks((prev) => ({
                    ...prev,
                    acceptTermsConditions: !prev.acceptTermsConditions,
                  }))
                }}
                name='acceptTermsConditions'
                sx={{padding: '0 9px 9px'}}
              />
            }
            label={
              <Typography variant='body2'>
                <Trans
                  components={[
                    <a
                      href={termsAndConditionsLink}
                      rel='noreferrer'
                      style={{textDecoration: 'underline', color: '#2D3E91'}}
                      target='_blank'
                    >
                      override
                    </a>,
                  ]}
                  values={{linkText: t('terms_and_conditions')}}
                >
                  {'I have read and accept the <0>{{linkText}}</0>.'}
                </Trans>
              </Typography>
            }
          />

          <FormControlLabel
            sx={{
              alignItems: 'flex-start',
            }}
            control={
              <Checkbox
                checked={checks.acceptPrivacyStatement}
                onChange={() => {
                  setChecks((prev) => ({
                    ...prev,
                    acceptPrivacyStatement: !prev.acceptPrivacyStatement,
                  }))
                }}
                name='acceptPrivacyStatement'
                sx={{padding: '0 9px 9px'}}
              />
            }
            label={
              <Typography variant='body2'>
                <Trans
                  i18nKey='create_account_consent_privacy_accept'
                  components={[
                    <a
                      href={privacyPolicyLink}
                      rel='noreferrer'
                      style={{textDecoration: 'underline', color: '#2D3E91'}}
                      target='_blank'
                    >
                      override
                    </a>,
                  ]}
                  values={{linkText: t('privacy_policy')}}
                >
                  {
                    'I have read and accept the <0>{{linkText}}</0> and agree to the processing of my personal data, including my sensitive personal data.'
                  }
                </Trans>
              </Typography>
            }
          />
        </Grid>
      </Grid>
      <MobileStickyActions>
        <Navigator
          showBackButton={false}
          label={t('create_account_consent_accept')}
          buttonDisable={
            updateLoading ||
            !(checks.acceptTermsConditions && checks.acceptPrivacyStatement)
          }
          onNextButtonClick={next}
          singleCentered
          fullWidth={isMobile}
        />
      </MobileStickyActions>
    </Box>
  )
}

export default Consent
